import {
    type AggregatePaginateResult,
    type ApprovalScopeEnum,
    type AutoMatchPurchaseOrdersRequest,
    type AutoMatchPurchaseOrdersResponse,
    type CommentItem,
    type PurchaseOrderDTO,
} from "@finway-group/shared/lib/models"
import {
    PURCHASE_ORDER_MODEL,
    type PurchaseOrderCloseConfirmationChecks,
    type PurchaseOrderExportReq,
    type PurchaseOrderExportRes,
} from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrder.api"
import { type PurchaseOrderApprovalProcessDTO } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderDTO"
import { PurchaseOrderActions } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatusAction.enum"
import { type QueryParamsObject, type TypeHints } from "@finway-group/shared/lib/models/queryParams/queryParams.types"
import axios, { type AxiosResponse } from "axios"
import { type QueryParams } from "~Shared/queries/consts"
import { type PurchaseOrder, type PurchaseOrderForLinking, type PurchaseOrderLinks } from "../types/PurchaseOrder.interfaces"

export enum ExtendedPurchaseOrderDeliveryStatusEnum {
    NONE = "none",
}
export interface PurchaseOrderTransitionRequest {
    action: string
    reason?: string
}

const poTypeHints: TypeHints = {
    purchaseOrderNumber: "string",
    description: "string",
    tags: "string",
}

export const PurchaseOrderApiService = {
    fetchPurchaseOrders: async ({
        params,
        signal,
    }: {
        params: QueryParamsObject & { forLinking?: boolean }
        signal?: AbortSignal
    }): Promise<AggregatePaginateResult<PurchaseOrder>> => {
        const requestParams: QueryParamsObject & { forLinking?: boolean } = {
            ...params,
            typeHint: poTypeHints,
        }

        if (requestParams.deliveryStatus) {
            const deliveryStatus = requestParams.deliveryStatus as { eq: string }
            // "none" delivery status is a UI-only option for service POs (they don't have delivery).
            if (deliveryStatus.eq === ExtendedPurchaseOrderDeliveryStatusEnum.NONE) {
                delete requestParams.deliveryStatus
                requestParams.isService = { is: true }
            } else {
                requestParams.isService = { is: false }
            }
        }

        const { data } = await axios.get<AggregatePaginateResult<PurchaseOrder>>(`/${PURCHASE_ORDER_MODEL}`, { params: requestParams, signal })
        return data
    },
    fetchPurchaseOrdersForLinking: ({ params, signal }: { params: QueryParams; signal?: AbortSignal }): Promise<AggregatePaginateResult<PurchaseOrderForLinking>> => {
        const { sortParams, ...rest } = params
        return PurchaseOrderApiService.fetchPurchaseOrders({ params: { ...rest, forLinking: true }, signal })
    },
    getPurchaseOrderById: async (purchaseOrderId: string) => {
        const { data } = await axios.get(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}`)
        return data
    },

    createAndSubmitPurchaseOrder: async (purchaseOrder: PurchaseOrderDTO) => {
        const { data } = await axios.post(`/${PURCHASE_ORDER_MODEL}`, purchaseOrder)
        return PurchaseOrderApiService.transitionPurchaseOrder(data._id, { action: PurchaseOrderActions.SUBMIT_FOR_APPROVAL })
    },
    createPurchaseOrder: async (purchaseOrder: PurchaseOrderDTO) => {
        const { data } = await axios.post(`/${PURCHASE_ORDER_MODEL}`, purchaseOrder)
        return data
    },
    updatePurchaseOrder: async (id: string, purchaseOrder: PurchaseOrderDTO) => {
        const { data } = await axios.put(`/${PURCHASE_ORDER_MODEL}/${id}`, purchaseOrder)
        return data
    },
    deletePurchaseOrder: async (purchaseOrderId: string) => {
        const { data } = await axios.delete(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}`)
        return data
    },

    transitionPurchaseOrder: async (purchaseOrderId: string, transition: PurchaseOrderTransitionRequest) => {
        const { data } = await axios.patch(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}/transition`, transition)
        return data
    },

    updateComments: async (purchaseOrderId: string, comments: Array<CommentItem>) => {
        const { data } = await axios.put(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}/comments`, { comments })
        return data
    },

    attachTagToPurchaseOrder: async (purchaseOrderId: string, tagId: string) => {
        const { data } = await axios.post(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}/tags/${tagId}`)
        return data
    },
    detachTagFromPurchaseOrder: async (purchaseOrderId: string, tagId: string) => {
        const { data } = await axios.delete(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}/tags/${tagId}`)
        return data
    },

    getPurchaseOrderLinks: async (purchaseOrderId: string) => {
        const { data } = await axios.get<PurchaseOrderLinks>(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}/links`)
        return data
    },

    fetchClosePurchaseOrderConfirmationChecks: async (purchaseOrderId: string) => {
        const { data } = await axios.get<PurchaseOrderCloseConfirmationChecks>(`/${PURCHASE_ORDER_MODEL}/${purchaseOrderId}/close-confirmation`)
        return data
    },

    autoMatchInvoiceItems: async (request: AutoMatchPurchaseOrdersRequest): Promise<AutoMatchPurchaseOrdersResponse> => {
        const { data } = await axios.post<AutoMatchPurchaseOrdersResponse>(`/${PURCHASE_ORDER_MODEL}/auto-match-invoice-items`, request)
        return data
    },
    buildApprovalProcesses: async (approvalProcessScopes: Array<ApprovalScopeEnum>, purchaseOrderData: PurchaseOrderApprovalProcessDTO) => {
        const { data } = await axios.post(`/${PURCHASE_ORDER_MODEL}/approval-processes`, {
            approvalProcessScopes,
            purchaseOrderData,
        })

        return data
    },
    exportAllPurchaseOrders: () => axios.post<PurchaseOrderExportRes, AxiosResponse<PurchaseOrderExportRes>, PurchaseOrderExportReq>(`/${PURCHASE_ORDER_MODEL}/export`),
}
